import { saveAs } from "file-saver";
import JSZip from "jszip";

const THUMBS_BUCKET_PREFIX = "thumbs-";

/**
 * Converte a URL assinada do bucket de thumbs (SD) na URL da imagem original (HD),
 * que fica no bucket de mesmo nome sem o prefixo "thumbs-" e não vem assinada.
 * Ex.: .../thumbs-qa.portalunisafety.com.br/x/y.jpeg?X-Amz-... -> .../qa.portalunisafety.com.br/x/y.jpeg
 * URLs que não são do bucket de thumbs são devolvidas como vieram.
 * @param {String} url - URL da imagem SD
 * @returns {String} URL da imagem HD
 */
export function getHdUrl(url) {
  try {
    const parsed = new URL(url);
    const [, bucket, ...key] = parsed.pathname.split("/");
    if (!bucket || !bucket.startsWith(THUMBS_BUCKET_PREFIX)) {
      return url;
    }
    parsed.pathname = `/${bucket.slice(THUMBS_BUCKET_PREFIX.length)}/${key.join("/")}`;
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch (error) {
    return url;
  }
}

/**
 * Busca a foto. No modo HD tenta a imagem original e, se ela não estiver acessível
 * (erro de rede/CORS ou status fora de 2xx), cai para a URL SD como veio.
 * @param {String} url - URL da imagem SD
 * @param {Boolean} hd - Tenta a imagem HD antes da SD
 * @returns {Promise<{response: Response, url: String, fellBack: Boolean}>} Resposta, a URL que a
 * atendeu e se foi preciso cair para a SD porque a HD existia como URL mas não estava acessível
 */
async function fetchPhoto(url, hd) {
  // Remove qualquer encoding da URL (decodifica caso esteja encoded)
  const sdUrl = decodeURIComponent(url);
  let fellBack = false;

  if (hd) {
    const hdUrl = decodeURIComponent(getHdUrl(url));
    // Sem URL HD distinta (ex.: não é do bucket de thumbs), segue direto para a SD
    if (hdUrl !== sdUrl) {
      try {
        const response = await fetch(hdUrl);
        if (response.ok) {
          return { response, url: hdUrl, fellBack };
        }
      } catch (error) {
        // HD inacessível, cai para a URL SD
      }
      fellBack = true;
    }
  }

  return { response: await fetch(sdUrl), url: sdUrl, fellBack };
}

/**
 * Monta o aviso para o usuário quando parte da galeria baixou em SD no lugar da HD.
 * @param {{total: Number, fallbackToSd: Number}} summary - Resumo devolvido por downloadGallery
 * @returns {String|null} Mensagem, ou null quando não há o que avisar
 */
export function getHdFallbackNotice({ total, fallbackToSd }) {
  if (!fallbackToSd) {
    return null;
  }
  if (fallbackToSd === total) {
    return "A versão HD não está disponível para estas fotos; a galeria foi baixada em SD.";
  }
  return `${fallbackToSd} de ${total} fotos vieram em SD porque a versão HD não está disponível.`;
}

/**
 * Baixa várias imagens e compacta em um arquivo ZIP
 * @param {Array} photos - Array de objetos com { url: string, name?: string }
 * @param {String} zipName - Nome do arquivo zip a ser baixado (sem extensão)
 * @param {Object} options
 * @param {Boolean} options.hd - Baixa a imagem original (HD) em vez da URL como veio (SD),
 * usando a SD como fallback quando a HD não estiver acessível
 * @param {Function} options.onProgress - Chamada com { current, total } ao iniciar (current 0) e a
 * cada foto processada, baixada ou não. A promise só resolve depois que o zip é gerado e salvo.
 * @returns {Promise<{total: Number, fallbackToSd: Number}>} Total de fotos pedidas e quantas
 * entraram no zip em SD porque a HD não estava acessível (sempre 0 fora do modo hd)
 */
export async function downloadGallery(
  photos,
  zipName = "galeria",
  { hd = false, onProgress = () => {} } = {}
) {
  if (!photos || photos.length === 0) {
    return { total: 0, fallbackToSd: 0 };
  }

  const zip = new JSZip();
  const imgFolder = zip.folder(zipName);
  const total = photos.length;
  let fallbackToSd = 0;

  onProgress({ current: 0, total });

  for (let i = 0; i < photos.length; i++) {
    let { url, name } = photos[i];
    try {
      const photo = await fetchPhoto(url, hd);
      url = photo.url;
      const blob = await photo.response.blob();

      // Mantém extensão original ou padrão .jpg
      const extension = url.split(".").pop().split(/#|\?/)[0] || "jpg";

      const fileName = name
        ? `${name}.${extension}`
        : `foto_${i + 1}.${extension}`;

      imgFolder.file(fileName, blob);
      if (photo.fellBack) {
        fallbackToSd++;
      }
    } catch (error) {
      // Erro ao baixar imagem ignorado propositalmente
    }
    onProgress({ current: i + 1, total });
  }

  // Gera e baixa o zip
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, `${zipName}.zip`);

  return { total, fallbackToSd };
}
